// ProgressTrackingService.ts
import { doc, updateDoc, getDoc, setDoc } from "firebase/firestore";
import { firestore } from "./Firebase";
import { getDefaultUserProgress } from "./UserProgressHelper";
import { getUserExperimentId } from "./ExpirmentHelper";

export interface ProgressUpdateData {
    contentId: string;
    contentType: 'courses' | 'interviews' | 'challenges' | 'resources' | 'tools' | 'actions';
    lessonId?: string; // For course lesson tracking - changed from number to string for navUrl
    challengeItemId?: string; // For individual challenge items
}

/**
 * Main function to mark content as completed
 */
export async function markContentAsCompleted(
    userId: string,
    updateData: ProgressUpdateData
): Promise<boolean> {
    try {
        const userDocRef = doc(firestore, "users", userId);
        const userDoc = await getDoc(userDocRef);

        let userData;
        if (!userDoc.exists()) {
            userData = getDefaultUserProgress(userId, getUserExperimentId());
        } else {
            userData = userDoc.data();
        }

        const updated = updateProgressData(userData, updateData, true);
        if (!updated) {
            return false;
        }

        await setDoc(userDocRef, userData, { merge: true });
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Mark content as incomplete (for undoing progress)
 */
export async function markContentAsIncomplete(
    userId: string,
    updateData: ProgressUpdateData
): Promise<boolean> {
    try {
        const userDocRef = doc(firestore, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            return false;
        }

        const userData = userDoc.data();
        const updated = updateProgressData(userData, updateData, false);
        
        if (!updated) {
            return false;
        }

        await setDoc(userDocRef, userData, { merge: true });
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Helper function to update progress data structure
 */
function updateProgressData(
    userData: any,
    updateData: ProgressUpdateData,
    completed: boolean
): boolean {
    const { contentId, contentType, lessonId, challengeItemId } = updateData;

    // Create missing content type arrays instead of failing
    if (!userData[contentType]) {
        userData[contentType] = [];
    }

    switch (contentType) {
        case 'courses':
            return updateCourseProgress(userData, contentId, completed, lessonId);
        case 'interviews':
        case 'resources':
        case 'tools':
        case 'actions':
            return updateGenericContentProgress(userData, contentType, contentId, completed);
        case 'challenges':
            return updateChallengeProgress(userData, contentId, completed, challengeItemId);
        default:
            return false;
    }
}

/**
 * Update course progress (including individual lessons)
 */
function updateCourseProgress(
    userData: any,
    courseId: string,
    completed: boolean,
    lessonId?: string // Changed from number to string
): boolean {
    const course = userData.courses.find((c: any) => c.id === courseId);
    if (!course) {
        return false;
    }

    if (lessonId !== undefined) {
        // Update specific lesson
        const lesson = course.lessons.find((l: any) => l.id === lessonId);
        if (!lesson) {
            const newLesson = {
                id: lessonId,
                hasCompleted: completed,
                displayInRoadMap: true
            };
            course.lessons = course.lessons || [];
            course.lessons.push(newLesson);
        } else {
            lesson.hasCompleted = completed;
        }

        // Check if all lessons are completed to mark course as complete
        if (completed) {
            const allLessonsComplete = course.lessons.every((l: any) => l.hasCompleted);
            if (allLessonsComplete) {
                course.hasCompleted = true;
            }
        } else {
            // If marking a lesson as incomplete, mark course as incomplete
            course.hasCompleted = false;
        }
    } else {
        // Update entire course
        course.hasCompleted = completed;
        // Also update all lessons
        course.lessons.forEach((lesson: any) => {
            lesson.hasCompleted = completed;
        });
    }
    return true;
}

/**
 * Update progress for interviews, resources, tools, actions
 */
function updateGenericContentProgress(
    userData: any,
    contentType: string,
    contentId: string,
    completed: boolean
): boolean {
    const content = userData[contentType].find((item: any) => item.id === contentId);
    if (!content) {
        const newContent = {
            id: contentId,
            hasCompleted: completed,
            displayInRoadMap: true
        };
        userData[contentType].push(newContent);
        return true;
    }
    
    content.hasCompleted = completed;
    return true;
}

/**
 * Update challenge progress
 */
function updateChallengeProgress(
    userData: any,
    challengeId: string,
    completed: boolean,
    challengeItemId?: string
): boolean {
    // Ensure challenges array exists
    if (!userData.challenges) {
        userData.challenges = [];
    }

    let challenge = userData.challenges.find((c: any) => c.id === challengeId);
    if (!challenge) {
        challenge = {
            id: challengeId,
            hasCompleted: false,
            displayInRoadMap: true,
            challengeItems: []
        };
        userData.challenges.push(challenge);
    }

    if (challengeItemId) {
        // Update specific challenge item
        let challengeItem = challenge.challengeItems.find((item: any) => item.id === challengeItemId);
        if (!challengeItem) {
            challengeItem = {
                id: challengeItemId,
                hasCompleted: completed
            };
            challenge.challengeItems.push(challengeItem);
        } else {
            challengeItem.hasCompleted = completed;
        }

        if (completed) {
            const allItemsComplete = challenge.challengeItems.every((item: any) => item.hasCompleted);
            if (allItemsComplete) {
                challenge.hasCompleted = true;
            }
        } else {
            challenge.hasCompleted = false;
        }
    } else {
        challenge.hasCompleted = completed;
        challenge.challengeItems.forEach((item: any) => {
            item.hasCompleted = completed;
        });
    }

    return true;
}

/**
 * Get user's progress for a specific lesson
 */
export async function getLessonProgress(
    userId: string,
    courseId: string,
    lessonId: string // Changed from number to string
): Promise<boolean> {
    try {
        const userDocRef = doc(firestore, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            return false;
        }

        const userData = userDoc.data();
        const course = userData.courses?.find((c: any) => c.id === courseId);
        if (!course) return false;

        const lesson = course.lessons?.find((l: any) => l.id === lessonId);
        return lesson?.hasCompleted || false;
    } catch (error) {
        return false;
    }
}

/**
 * Get user's progress for a specific challenge item
 */
export async function getChallengeItemProgress(
    userId: string,
    challengeId: string,
    challengeItemId: string
): Promise<boolean> {
    try {
        const userDocRef = doc(firestore, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            return false;
        }

        const userData = userDoc.data();
        const challenge = userData.challenges?.find((c: any) => c.id === challengeId);
        if (!challenge) return false;

        const challengeItem = challenge.challengeItems?.find((item: any) => item.id === challengeItemId);
        return challengeItem?.hasCompleted || false;
    } catch (error) {
        return false;
    }
}

/**
 * Convenience functions for specific content types
 */

// Course and lesson tracking
export const markLessonAsCompleted = (userId: string, courseId: string, lessonId: string) => // Changed from number to string
    markContentAsCompleted(userId, { contentId: courseId, contentType: 'courses', lessonId });

export const markLessonAsIncomplete = (userId: string, courseId: string, lessonId: string) => // Changed from number to string
    markContentAsIncomplete(userId, { contentId: courseId, contentType: 'courses', lessonId });

export const markCourseAsCompleted = (userId: string, courseId: string) =>
    markContentAsCompleted(userId, { contentId: courseId, contentType: 'courses' });

// Interview tracking
export const markInterviewAsCompleted = (userId: string, interviewId: string) =>
    markContentAsCompleted(userId, { contentId: interviewId, contentType: 'interviews' });

export const markInterviewAsIncomplete = (userId: string, interviewId: string) =>
    markContentAsIncomplete(userId, { contentId: interviewId, contentType: 'interviews' });

// Challenge tracking
export const markChallengeItemAsCompleted = (userId: string, challengeId: string, challengeItemId: string) =>
    markContentAsCompleted(userId, { contentId: challengeId, contentType: 'challenges', challengeItemId });

export const markChallengeItemAsIncomplete = (userId: string, challengeId: string, challengeItemId: string) =>
    markContentAsIncomplete(userId, { contentId: challengeId, contentType: 'challenges', challengeItemId });

export const markChallengeAsCompleted = (userId: string, challengeId: string) =>
    markContentAsCompleted(userId, { contentId: challengeId, contentType: 'challenges' });

// Resource tracking
export const markResourceAsCompleted = (userId: string, resourceId: string) =>
    markContentAsCompleted(userId, { contentId: resourceId, contentType: 'resources' });

export const markResourceAsIncomplete = (userId: string, resourceId: string) =>
    markContentAsIncomplete(userId, { contentId: resourceId, contentType: 'resources' });

// Tool tracking
export const markToolAsCompleted = (userId: string, toolId: string) =>
    markContentAsCompleted(userId, { contentId: toolId, contentType: 'tools' });

export const markToolAsIncomplete = (userId: string, toolId: string) =>
    markContentAsIncomplete(userId, { contentId: toolId, contentType: 'tools' });

// Action tracking
export const markActionAsCompleted = (userId: string, actionId: string) =>
    markContentAsCompleted(userId, { contentId: actionId, contentType: 'actions' });

export const markActionAsIncomplete = (userId: string, actionId: string) =>
    markContentAsIncomplete(userId, { contentId: actionId, contentType: 'actions' }); 